import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import {
  acquire,
  getEntitlement,
  getJob,
  isGenerationCeilingError,
  isMonthlyCapReachedError,
  isReelNameRequiredError,
  isTwitchSignInRequiredError,
  isUpgradeRequiredError,
} from '../api/rttsClient';
import type { Entitlement, HighlightJob } from '../api/types';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { setMockAuthState } from '../api/mockState';
import ReconnectTwitchCard from '../components/ReconnectTwitchCard';
import ReelResultPanel from '../components/ReelResultPanel';
import StudioProgressPanel from '../components/StudioProgressPanel';
import { ReportProblemButton } from '../components/ReportProblemModal';
import { STUDIO_COPY, STUDIO_DOCUMENT_TITLES, formatGenerateFailedCopy, isNoCandidatesError, isStudioBillingEnabled, isStudioUseMock } from '../config';
import { useJobPolling } from '../hooks/useJobPolling';
import { useStudioAuth } from '../hooks/useStudioAuth';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { formatVodDate, isJobExpired } from '../utils/format';

export function formatReelContextLine(job: {
  vod_created_at?: string | null;
  created_at?: string | null;
}): string {
  const iso = job.vod_created_at || job.created_at;
  if (!iso) return STUDIO_COPY.reelContextUndated;
  return STUDIO_COPY.reelContextTemplate.replace(
    '{stream date}',
    formatVodDate(iso)
  );
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; job: HighlightJob; entitlement: Entitlement | null }
  | { kind: 'reconnect' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string; errorCode?: string | null };

/**
 * Result view addressable by job id — supports refresh / return re-entry.
 * Re-fetches getJob for a fresh signed_url (MVP expiry path).
 * In-progress jobs poll live status (same as generate, without cancelling).
 */
const StudioReelPage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.reel);
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { status, isLoggedOut, setMockState, refreshMock } = useStudioAuth();
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });
  const [pollEnabled, setPollEnabled] = useState(false);
  const [recutBusy, setRecutBusy] = useState(false);
  const [recutError, setRecutError] = useState<string | null>(null);

  const pollState = useJobPolling(jobId ?? null, pollEnabled);

  const load = useCallback(async () => {
    if (!jobId) return;
    setLoadState({ kind: 'loading' });
    setPollEnabled(false);
    try {
      const [{ job }, entitlement] = await Promise.all([
        getJob(jobId),
        getEntitlement().catch(() => null),
      ]);

      if (job.status === 'failed') {
        setLoadState({
          kind: 'error',
          message: formatGenerateFailedCopy(job.error_code),
          errorCode: job.error_code,
        });
        return;
      }

      if (job.status !== 'delivered') {
        setLoadState({ kind: 'ready', job, entitlement });
        setPollEnabled(true);
        return;
      }

      if (!job.signed_url) {
        setLoadState({ kind: 'expired' });
        return;
      }

      // If expires_at already passed, re-fetch once more for a fresh URL.
      if (isJobExpired(job.expires_at)) {
        const refreshed = await getJob(jobId);
        if (!refreshed.job.signed_url) {
          setLoadState({ kind: 'expired' });
          return;
        }
        setLoadState({
          kind: 'ready',
          job: refreshed.job,
          entitlement,
        });
        return;
      }

      setLoadState({ kind: 'ready', job, entitlement });
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setLoadState({ kind: 'reconnect' });
        return;
      }
      setLoadState({ kind: 'expired' });
    }
  }, [jobId, refreshMock]);

  useEffect(() => {
    if (isLoggedOut || status === 'loading' || status === 'stale_link') return;
    void load();
  }, [isLoggedOut, status, load]);

  useEffect(() => {
    if (!pollEnabled) return;
    if (pollState.kind === 'delivered') {
      setPollEnabled(false);
      setLoadState((prev) => {
        const entitlement = prev.kind === 'ready' ? prev.entitlement : null;
        return { kind: 'ready', job: pollState.job, entitlement };
      });
    } else if (pollState.kind === 'failed') {
      setPollEnabled(false);
      setLoadState({
        kind: 'error',
        message: formatGenerateFailedCopy(pollState.job.error_code),
        errorCode: pollState.job.error_code,
      });
    } else if (pollState.kind === 'polling') {
      setLoadState((prev) => {
        const entitlement = prev.kind === 'ready' ? prev.entitlement : null;
        return { kind: 'ready', job: pollState.job, entitlement };
      });
    } else if (pollState.kind === 'awaiting_curation') {
      setPollEnabled(false);
      setLoadState((prev) => {
        const entitlement = prev.kind === 'ready' ? prev.entitlement : null;
        return { kind: 'ready', job: pollState.job, entitlement };
      });
    } else if (pollState.kind === 'reconnect') {
      setPollEnabled(false);
      setLoadState({ kind: 'reconnect' });
    }
  }, [pollState, pollEnabled]);

  if (status === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  if (status === 'stale_link' || loadState.kind === 'reconnect') {
    return (
      <div>
        <ReconnectTwitchCard
          onReconnect={() => {
            const shouldNavigate = beginTwitchConnect({
              returnTo: `/studio/reel/${jobId ?? ''}`,
            });
            if (shouldNavigate) {
              setMockState('linked');
              void load();
            }
          }}
        />
      </div>
    );
  }

  if (!jobId) {
    return <Navigate to="/studio/vods" replace />;
  }

  if (loadState.kind === 'loading') {
    return (
      <div className="flex justify-center py-16" data-testid="studio-reel-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (loadState.kind === 'expired') {
    return (
      <div
        className="text-center space-y-4 py-10"
        data-testid="studio-reel-expired"
      >
        <h1 className="text-2xl font-semibold">Link expired</h1>
        <p className="text-[var(--studio-muted)]">{STUDIO_COPY.linkExpired}</p>
        <Link to="/studio/vods" className="studio-btn-primary no-underline inline-flex">
          Back to VODs
        </Link>
      </div>
    );
  }

  if (loadState.kind === 'error') {
    const noPlays = isNoCandidatesError(loadState.errorCode);
    return (
      <div
        className="text-center space-y-4 py-10"
        role="alert"
        data-testid="studio-reel-failed"
        data-error-code={loadState.errorCode || ''}
      >
        <p>{loadState.message}</p>
        <p className="text-xs text-[var(--studio-muted)]">
          {STUDIO_COPY.failedCutNoChargeNote}
        </p>
        <ReportProblemButton
          label={STUDIO_COPY.reportProblemReel}
          testId="studio-reel-report-problem"
          prefill={{
            jobId,
            reelState: 'failed',
            errorCode: loadState.errorCode || undefined,
          }}
        />
        <Link
          to="/studio/vods"
          className="studio-btn-primary no-underline inline-flex"
          data-testid={
            noPlays ? 'studio-reel-try-another-vod' : 'studio-reel-back-vods'
          }
        >
          {noPlays ? STUDIO_COPY.noCandidatesCta : 'Back to VODs'}
        </Link>
      </div>
    );
  }

  if (loadState.kind === 'ready' && loadState.job.status === 'awaiting_curation') {
    return (
      <div
        className="text-center space-y-4 py-10"
        data-testid="studio-reel-awaiting-curation"
      >
        <p className="text-[var(--studio-muted)]">
          This reel is waiting for play picks. Continue cutting from Generate.
        </p>
        <Link
          to="/studio/reels"
          className="studio-btn-ghost no-underline inline-flex"
        >
          {STUDIO_COPY.reelsLeaveCta}
        </Link>
      </div>
    );
  }

  if (loadState.kind === 'ready' && loadState.job.status !== 'delivered') {
    return (
      <StudioProgressPanel
        status={loadState.job.status}
        detectWindowsTotal={loadState.job.detect_windows_total}
        detectWindowsDone={loadState.job.detect_windows_done}
        detectWindowsFailed={loadState.job.detect_windows_failed}
        vodDurationSec={
          loadState.job.source_duration_sec ?? loadState.job.reel_duration_sec
        }
        overdue={pollState.kind === 'polling' && pollState.overdue}
        allowLeave
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="studio-reel-context" data-testid="studio-reel-context">
        {formatReelContextLine(loadState.job)}
      </p>
      <ReelResultPanel
      job={loadState.job}
      entitlement={loadState.entitlement}
      alreadyExists={Boolean(loadState.job.accepted)}
      recutBusy={recutBusy}
      recutError={recutError}
      onRefineStarted={(nextJob) => {
        setLoadState((prev) =>
          prev.kind === 'ready'
            ? { kind: 'ready', job: nextJob, entitlement: prev.entitlement }
            : { kind: 'ready', job: nextJob, entitlement: null }
        );
        setPollEnabled(true);
      }}
      onAcceptStarted={(nextJob) => {
        setLoadState((prev) =>
          prev.kind === 'ready'
            ? { kind: 'ready', job: nextJob, entitlement: prev.entitlement }
            : { kind: 'ready', job: nextJob, entitlement: null }
        );
        setPollEnabled(true);
      }}
      onEntitlementRefresh={async () => {
        try {
          const entitlement = await getEntitlement();
          setLoadState((prev) =>
            prev.kind === 'ready'
              ? { kind: 'ready', job: prev.job, entitlement }
              : prev
          );
        } catch {
          // keep prior
        }
      }}
      onRecut={
        loadState.job.provider_vod_id
          ? async (name, options) => {
              const vodId = loadState.job.provider_vod_id;
              if (!vodId || recutBusy) return;
              setRecutBusy(true);
              setRecutError(null);
              try {
                const { job } = await acquire(vodId, {
                  recut: true,
                  reelName: name,
                  force: Boolean(options?.force),
                });
                setLoadState((prev) =>
                  prev.kind === 'ready'
                    ? {
                        kind: 'ready',
                        job,
                        entitlement: prev.entitlement,
                      }
                    : { kind: 'ready', job, entitlement: null }
                );
                setPollEnabled(true);
              } catch (error) {
                if (isUpgradeRequiredError(error)) {
                  if (isStudioBillingEnabled()) {
                    navigate('/studio/upgrade', { replace: true });
                  } else {
                    setRecutError(STUDIO_COPY.betaLimitReached);
                  }
                  return;
                }
                if (isGenerationCeilingError(error)) {
                  setRecutError('generation_ceiling_reached');
                  return;
                }
                if (isMonthlyCapReachedError(error)) {
                  setRecutError(
                    isStudioBillingEnabled() && error.period_resets_at
                      ? `Monthly cap reached — resets ${new Date(error.period_resets_at).toLocaleDateString()}.`
                      : STUDIO_COPY.betaLimitReached
                  );
                  return;
                }
                if (isTwitchSignInRequiredError(error)) {
                  setLoadState({ kind: 'reconnect' });
                  return;
                }
                if (isReelNameRequiredError(error)) {
                  setRecutError(STUDIO_COPY.reelNameRequired);
                  return;
                }
                setRecutError('Could not start a re-cut. Please try again.');
              } finally {
                setRecutBusy(false);
              }
            }
          : undefined
      }
    />
    </div>
  );
};

export default StudioReelPage;
